import { Component, OnInit, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Auth, authState } from '@angular/fire/auth';
import { Firestore, doc, getDoc, collection, query, where, limit, getDocs } from '@angular/fire/firestore';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';
import { WhatsAppAccountService, WhatsAppSignupPrefill, buildWhatsAppSignupPrefill } from '../../core/services/whatsapp-account.service';
import { countryByDial } from '../../core/config/countries';
import { OrganizationService } from '../../core/services/organization.service';
import { OnboardingService } from '../../core/services/onboarding.service';
import { OrgContextService } from '../../core/services/org-context.service';
import { ONBOARDING_STEPS } from '../../core/config/onboarding-steps';
import { explainWhatsAppLinkError } from '../../core/config/whatsapp-link-errors';
import { WhatsappSetupChecklistComponent } from '../../shared/components/whatsapp-setup-checklist/whatsapp-setup-checklist.component';
import { SetupContext } from '../../core/utils/whatsapp-setup-progress';

@Component({
    selector: 'app-whatsapp-setup',
    standalone: true,
    imports: [CommonModule, WhatsappSetupChecklistComponent],
    templateUrl: './whatsapp-setup.component.html',
    styleUrls: ['./whatsapp-setup.component.scss']
})
export class WhatsappSetupComponent implements OnInit {

    currentUser: any = null;
    organizationId: string = '';
    organizationName: string = '';
    isConnecting: boolean = false;
    /**
     * Starts true: until the account has been read, an already-connected
     * organisation would otherwise be shown the "connect a number" picker and
     * could start a signup that replaces its account.
     */
    isLoading: boolean = true;
    whatsappAccount: any = null;
    openFaqIndex: number | null = null;

    /**
     * The organisation record, fetched up front purely to prefill signup.
     *
     * Read on init rather than on click because `FB.login` opens a pop-up, and
     * a browser only permits that from inside a user gesture. Awaiting a
     * Firestore read first loses the gesture and the pop-up is blocked. The
     * prefill is a convenience, so a cache that has not arrived yet costs a
     * few pre-filled fields — never the connection itself.
     */
    private orgDataCache: any = null;

    /**
     * What the checklist cannot work out from the account alone.
     *
     * `hasPaymentMethod` is deliberately left undefined: reading it needs BSP
     * permissions Echo does not hold, so the checklist reports it as unknown
     * and asks rather than claiming either way.
     */
    checklistContext: SetupContext = {};

    /**
     * Whether this organisation is connected to Planning Center.
     *
     * The "what you can do" list promises automations driven by Planning
     * Center groups, lists, and service teams. A standalone organisation has
     * none of those, so promising them during onboarding sells a feature it
     * cannot use.
     */
    hasPco: boolean = true;

    // Step progress tracking
    currentStep: number = 2;
    totalSteps: number = ONBOARDING_STEPS.length;
    progressPercentage: number = 0;

    private auth = inject(Auth);
    private authState$ = authState(this.auth);
    private firestore = inject(Firestore);
    private injector = inject(EnvironmentInjector);
    private whatsappService = inject(WhatsAppAccountService);
    private organizationService = inject(OrganizationService);
    private onboardingService = inject(OnboardingService);
    private orgContext = inject(OrgContextService);

    constructor(private router: Router) { }

    async ngOnInit(): Promise<void> {
        this.currentUser = await firstValueFrom(this.authState$);

        await this.orgContext.load();
        this.hasPco = this.orgContext.hasPco();

        if (this.currentUser) {
            // Check if user is an owner - only owners can set up WhatsApp
            const userDocRef = doc(this.firestore, 'users', this.currentUser.uid);
            const userDocSnap = await runInInjectionContext(this.injector, () => getDoc(userDocRef));

            if (userDocSnap.exists()) {
                const userRole = userDocSnap.data()['role'];

                if (userRole !== 'owner') {
                    console.warn('User is not an owner - cannot set up WhatsApp');
                    await Swal.fire({
                        icon: 'info',
                        title: 'Owner Access Required',
                        html: 'Only organization owners can connect WhatsApp.<br><br>Your organization owner will complete this setup.',
                        confirmButtonText: 'Return to Dashboard'
                    });
                    this.router.navigate(['/']);
                    return;
                }
            }

            // Membership comes from the user document, not the Planning Center
            // sign-in claim: a standalone organisation has no such claim, and
            // reading it left organizationId empty, which then produced
            // "Invalid document reference" on every Firestore call below.
            const setup = await this.onboardingService.getSetupContext(
                this.currentUser.uid,
                'whatsapp-setup'
            );
            this.organizationId = setup.organizationId;
            this.organizationName = setup.organizationName;
            this.currentStep = setup.currentStep;
            this.totalSteps = setup.totalSteps;
            this.progressPercentage = setup.progressPercentage;
        }


        // Both of these exist so the connect click can reach FB.login without
        // awaiting anything. See orgDataCache and preloadFacebookSdk.
        this.whatsappService.preloadFacebookSdk();
        this.getOrganizationData().then(org => { this.orgDataCache = org; });

        this.loadChecklistContext();

        // Load existing WhatsApp account if connected
        this.loadWhatsAppAccount();
    }

    /**
     * Fill in the two checklist facts the account document does not carry.
     *
     * Both are best-effort. A failed probe leaves the task showing as
     * outstanding, which is the safe way round — telling somebody a step is
     * done when it is not is how a campaign gets scheduled that cannot send.
     */
    private async loadChecklistContext() {
        if (!this.organizationId) return;

        try {
            const state = await this.onboardingService.getOnboardingState(this.organizationId);
            // Completed, not merely reached: `completedSteps` records a skip
            // exactly like a finish, which is what used to make every
            // organisation look fully set up regardless of what they had done.
            const outcome = state?.stepOutcomes?.['business-details-setup']?.outcome;
            this.checklistContext = {
                ...this.checklistContext,
                hasBusinessProfile: outcome === 'completed'
            };
        } catch (error) {
            console.error('Could not read onboarding state for the checklist', error);
        }

        try {
            const approved = await runInInjectionContext(this.injector, () => getDocs(query(
                collection(this.firestore, 'organizations', this.organizationId, 'whatsappTemplates'),
                where('status', '==', 'APPROVED'),
                limit(1)
            )));
            this.checklistContext = {
                ...this.checklistContext,
                hasApprovedTemplate: !approved.empty
            };
        } catch (error) {
            console.error('Could not check for an approved template', error);
        }
    }

    async loadWhatsAppAccount() {
        if (!this.organizationId) {
            this.isLoading = false;
            return;
        }

        this.isLoading = true;
        this.whatsappService.getWhatsAppAccount(this.organizationId).subscribe({
            next: (account) => {
                this.whatsappAccount = account;
                this.isLoading = false;
                // Check payment method status if connected and not already loaded
                if (account && account.status === 'connected' && !(account as any).paymentMethod) {
                    this.checkPaymentMethod();
                }
            },
            error: (error) => {
                console.error('Error loading WhatsApp account:', error);
                this.isLoading = false;
            }
        });
    }

    /**
     * Which route the customer has told us their number needs.
     *
     * Asked before the dialog opens, because `featureType` is fixed when
     * FB.login is called and cannot be changed once Meta's screens are up. It
     * is also the question customers most reliably get wrong on their own: the
     * three cases look identical from the outside and only one of them is the
     * plain "connect a number" flow.
     */
    numberRoute: 'new' | 'business-app' | 'personal' | 'existing-waba' | null = null;

    /** Show the route picker. */
    chooseRoute(route: 'new' | 'business-app' | 'personal' | 'existing-waba') {
        this.numberRoute = route;
    }

    /** Back to the route picker without starting anything. */
    clearRoute() {
        this.numberRoute = null;
    }

    /**
     * Start Embedded Signup for a number already live in the WhatsApp Business app.
     *
     * The same dialog, with Meta's Business-app branch offered.
     */
    connectExistingWhatsAppApp() {
        this.connectWhatsApp({ coexistence: true });
    }

    /**
     * Ask Solenex to link a number that is already on a WhatsApp Business Account.
     *
     * Embedded Signup cannot adopt one — it onboards a number, it does not take
     * over an existing one — so this case needs an operator. Until the console
     * screen exists (W5) this opens a prepared support mail rather than leaving
     * the customer at a dead end.
     */
    requestAssistedLink() {
        const subject = encodeURIComponent(`WhatsApp link request — ${this.organizationName}`);
        const body = encodeURIComponent(
            `Organisation: ${this.organizationName}\n` +
            `Organisation ID: ${this.organizationId}\n\n` +
            "My WhatsApp number is already connected to a WhatsApp Business Account, " +
            "so it cannot be added through the usual sign-up.\n\n" +
            "Number to connect: \n"
        );
        window.location.href = `mailto:echo@solenex.it?subject=${subject}&body=${body}`;
    }

    connectWhatsApp(options?: { coexistence?: boolean }) {
        if (!this.organizationId) {
            console.error('No organization ID available');
            return;
        }

        this.isConnecting = true;

        // Nothing may be awaited between the click and FB.login, or the
        // browser stops treating this as a user gesture and blocks the pop-up
        // — silently, with FB.login never calling back at all.
        const setupData = this.buildSetupData(this.orgDataCache);
        const signup = this.whatsappService.openEmbeddedSignup(setupData, options);

        Swal.fire({
            title: 'Connecting to your WhatsApp Business account...',
            text: 'Please wait...',
            allowOutsideClick: false,
            allowEscapeKey: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        signup.then(
            ({ code, wabaId, phoneNumberId, coexistence }) => {
                // The code is never logged: it exchanges for a business access
                // token and has a 30-second TTL.
                this.exchangeSignupCode(code, this.organizationId, wabaId, {
                    phoneNumberId,
                    coexistence
                });
            },
            (error) => {
                this.isConnecting = false;
                Swal.close();

                // Recorded either way. A cancellation still says where the
                // funnel leaks, and an error carries the session id Meta
                // support asks for first.
                this.whatsappService.recordSignupAbandonment(
                    this.organizationId, error, options?.coexistence === true
                );

                // A cancellation is a decision, not a fault — it gets no dialog.
                // Anything else did go wrong, and used to be reported under the
                // same "Connection Cancelled" heading, which sent people hunting
                // in Meta Business Manager for a problem that was not there.
                if (error?.cancelled) {
                    console.info('WhatsApp signup cancelled', error?.detail);
                    return;
                }

                console.error('Signup error:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'WhatsApp Connection Failed',
                    // The session id is what Meta support asks for first, so it
                    // is shown rather than buried in the console.
                    html: `<p>${error?.message || 'WhatsApp could not be connected.'}</p>` +
                        (error?.detail?.sessionId ?
                            `<p class="text-muted small mt-3">If you contact support, quote this
                             reference: <code>${error.detail.sessionId}</code></p>` :
                            ''),
                    confirmButtonColor: '#556ee6'
                });
            }
        );
    }

    exchangeSignupCode(
        code: string,
        organizationId: string,
        wabaId?: string | null,
        session?: { phoneNumberId?: string | null; coexistence?: boolean }
    ) {
        this.isConnecting = true;

        this.whatsappService.exchangeSignupCode(code, organizationId, wabaId, session).subscribe({
            next: (result) => {
                this.isConnecting = false;
                console.log('Exchange result:', result);
                Swal.close();

                // Build phone number list HTML
                const phoneListHtml = result.phoneNumbers && result.phoneNumbers.length > 0
                    ? `<div class="text-start mt-3 mb-3">
                        <p class="mb-2"><strong>${result.phoneNumbers.length} phone number(s) found:</strong></p>
                        <ul class="list-unstyled">
                            ${result.phoneNumbers.map((p: any) =>
                        `<li class="mb-1"><i class="bx bxs-phone text-success me-1"></i> ${p.displayPhoneNumber} <span class="text-muted">- ${p.verifiedName || 'Pending'}</span></li>`
                    ).join('')}
                        </ul>
                       </div>`
                    : '';

                Swal.fire({
                    icon: 'success',
                    title: 'Connected!',
                    html: `<p>WhatsApp Business Account connected successfully.</p>
                           ${phoneListHtml}
                           <div class="alert alert-warning text-start mt-3" style="font-size: 0.9em;">
                             <i class="bx bx-credit-card me-1"></i>
                             <strong>Important:</strong> A valid payment method must be added to your WhatsApp Business Account in Meta Business Manager for messaging to work.
                           </div>`,
                    showCancelButton: true,
                    confirmButtonText: '<i class="bx bx-credit-card me-1"></i> Add Payment Method',
                    cancelButtonText: 'I\'ll do this later',
                    confirmButtonColor: '#556ee6',
                    cancelButtonColor: '#74788d'
                }).then((swalResult) => {
                    if (swalResult.isConfirmed) {
                        this.openMetaPaymentSettings();
                    }
                });

                // Reload WhatsApp account data
                this.loadWhatsAppAccount();

                // Clear query parameters from URL
                window.history.replaceState({}, document.title, window.location.pathname);
            },
            error: (error) => {
                this.isConnecting = false;
                console.error('Exchange error:', error);
                Swal.close();

                // An account already linked elsewhere is a refusal, not a fault —
                // saying only "Connection Failed" sends people hunting in Meta
                // Business Manager for a problem that is not there.
                const explained = explainWhatsAppLinkError(error);

                Swal.fire({
                    icon: 'error',
                    title: explained.title,
                    text: explained.text,
                    confirmButtonColor: '#556ee6'
                });
            }
        });
    }

    async getOrganizationData(): Promise<any> {
        try {
            const org = await firstValueFrom(this.organizationService.getOrganization(this.organizationId));
            return org;
        } catch (error) {
            console.error('Error loading organization data:', error);
            return null;
        }
    }

    /**
     * Build the Embedded Signup prefill.
     *
     * This used to be a second, divergent implementation that read
     * `address.line1` (the data is `street1`), emitted `zipCode` (Meta expects
     * `zipPostal`) and passed a dialling code as the country. Meta ignores keys
     * it does not recognise, so those fields just arrived blank.
     */
    buildSetupData(orgData: any): WhatsAppSignupPrefill {
        return buildWhatsAppSignupPrefill(
            orgData,
            this.organizationName,
            this.auth.currentUser?.email || null,
            countryByDial(orgData?.countryCode)?.iso ?? null
        );
    }

    getPhoneNumbersForWaba(wabaId: string): string[] {
        if (!this.whatsappAccount?.phoneNumbers) return [];
        return this.whatsappAccount.phoneNumbers
            .filter((phone: any) => phone.wabaId === wabaId)
            .map((phone: any) => phone.displayPhoneNumber);
    }

    /**
     * Opens the Meta Business Manager payment settings page for the first WABA
     */
    openMetaPaymentSettings() {
        const wabaId = this.whatsappAccount?.wabaIds?.[0];
        const businessId = (this.whatsappAccount as any)?.businessId;

        // Direct link to Meta Business Manager WhatsApp settings
        let paymentUrl = 'https://business.facebook.com/latest/settings/whatsapp_account/';

        if (businessId && wabaId) {
            paymentUrl += `?business_id=${businessId}&selected_asset_id=${wabaId}&selected_asset_type=whatsapp-business-account`;
        } else if (businessId) {
            paymentUrl += `?business_id=${businessId}`;
        }

        window.open(paymentUrl, '_blank', 'noopener,noreferrer');
    }

    checkPaymentMethod() {
        if (!this.organizationId) return;
        this.whatsappService.checkPaymentMethod(this.organizationId).subscribe({
            next: (result) => {
                if (this.whatsappAccount) {
                    this.whatsappAccount.paymentMethod = result;
                }
            },
            error: (error) => {
                console.error('Error checking payment method:', error);
            }
        });
    }

    async skip() {
        await this.advance('skipped');
    }

    /**
     * Leave the step after WhatsApp actually connected.
     *
     * Separate from `skip()` because the two record different outcomes, and
     * both buttons used to call `skip()` — so every organisation that connected
     * successfully was filed as having skipped the step, and showed that way in
     * the platform console. That is the one thing `stepOutcomes` exists to tell
     * apart from a genuine skip.
     */
    async complete() {
        await this.advance('completed');
    }

    /**
     * Record the outcome and move to whatever step is next.
     *
     * @param outcome How the customer left this step.
     */
    private async advance(outcome: 'completed' | 'skipped') {
        await this.onboardingService.completeStep(this.organizationId, 'whatsapp-setup', outcome);
        const nextStep = await this.onboardingService.resolveNextStep(this.organizationId);
        this.router.navigate([nextStep ? nextStep.route : '/']);
    }

    toggleFaq(index: number) {
        this.openFaqIndex = this.openFaqIndex === index ? null : index;
    }
}
